/**
 * Platform detection — distinguishes the iOS/Android Capacitor build from
 * the web bundle. Used to:
 *   - Skip the marketing landing page on native (open straight to sign-in)
 *   - Hide pricing / upgrade CTAs on native (App Store "reader app" rules
 *     forbid showing prices or external checkout — Apple WILL reject)
 *   - Tweak layout for the iOS status bar / Dynamic Island
 *
 * On web, all functions return false. On native, isNative() returns true
 * and isIOS()/isAndroid() reflect the platform.
 *
 * Capacitor exposes `window.Capacitor` only when the app is running
 * inside the native shell. Importing the SDK directly would force every
 * web user to load a few KB of native-only code; checking the global is
 * lighter.
 */

export function isNative() {
  if (typeof window === "undefined") return false;
  const cap = window.Capacitor;
  if (!cap) return false;
  // Capacitor 5+ exposes isNativePlatform(); older versions don't.
  if (typeof cap.isNativePlatform === "function") return cap.isNativePlatform();
  // Fallback: presence of getPlatform() returning ios/android means native.
  if (typeof cap.getPlatform === "function") {
    const p = cap.getPlatform();
    return p === "ios" || p === "android";
  }
  return false;
}

export function getPlatform() {
  if (typeof window === "undefined") return "web";
  const cap = window.Capacitor;
  if (cap?.getPlatform) {
    try { return cap.getPlatform(); } catch { /* ignore */ }
  }
  return "web";
}

export function isIOS() { return getPlatform() === "ios"; }
export function isAndroid() { return getPlatform() === "android"; }

/**
 * Touch-first device (covers the native app AND mobile Safari). Used to gate
 * `autoFocus` in modals — on touch, autofocusing an input slams the keyboard
 * up before the sheet's enter animation settles, and often over the field
 * itself. Desktop keeps the convenience focus.
 */
export const IS_TOUCH = typeof window !== "undefined"
  && ((window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
    || (navigator.maxTouchPoints || 0) > 0);

/* The native app's own version (Xcode "Version", e.g. "1.0"), read once at
   boot by loadNativeAppVersion(). Lets a feature that needs App Store review
   switch on only in the reviewed build, whatever OTA bundle is running. */
let _nativeVersion = null;
export function nativeAppVersion() { return _nativeVersion; }
export async function loadNativeAppVersion() {
  if (!isNative()) return null;
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    _nativeVersion = info?.version || null;
  } catch { /* stays unknown: version-gated features stay off */ }
  return _nativeVersion;
}
